import type {
  CaseFilters,
  PathologistReviewQueueFilters,
  PathologistReviewQueueScope,
} from "../../services";
import {
  ASSIGNED_PATHOLOGIST_COLUMN,
  TC_REVIEW_ACTION_COLUMN,
  type PathologistReviewQueueCase,
  type QueueColumnDef,
} from "./queueColumnConfig";
import { compareCaseNumbers } from "../../utils/caseNumberSort";

export interface QueueSortState {
  column: string;
  direction: "asc" | "desc";
}

export function getQueueCasePrefixKey(caseNumber: string): string {
  const trimmed = caseNumber.trim();
  if (/^[0-9]{2}-G-[0-9]+(-AS[0-9]+)?$/.test(trimmed)) return "G";
  const prefix = trimmed.match(/^([A-Za-z]+)[0-9]*-[0-9]+(-AS[0-9]+)?$/)?.[1];
  return prefix && prefix.length <= 16 ? prefix.toUpperCase() : "__OTHER__";
}

export function advanceReviewSorts(
  sorts: QueueSortState[],
  column: string
): QueueSortState[] {
  const index = sorts.findIndex((sort) => sort.column === column);
  if (index < 0) return [...sorts, { column, direction: "asc" }];
  if (sorts[index].direction === "desc") {
    return sorts.filter((sort) => sort.column !== column);
  }
  return sorts.map((sort) =>
    sort.column === column ? { column, direction: "desc" } : sort
  );
}

type CompareQueueColumn = (
  first: PathologistReviewQueueCase,
  second: PathologistReviewQueueCase,
  column: string,
  direction: "asc" | "desc"
) => number;

export function sortReviewQueueCases(
  cases: PathologistReviewQueueCase[],
  sorts: QueueSortState[],
  compareColumn: CompareQueueColumn
): PathologistReviewQueueCase[] {
  if (sorts.length === 0) return cases;
  return [...cases].sort((first, second) => {
    const sortComparison = sorts.reduce((result, sort) => {
      if (result !== 0) return result;
      return sort.column === "caseNumber"
        ? compareCaseNumbers(first.caseNumber, second.caseNumber) *
            (sort.direction === "asc" ? 1 : -1)
        : compareColumn(first, second, sort.column, sort.direction);
    }, 0);
    if (sortComparison !== 0) return sortComparison;
    return (
      compareCaseNumbers(first.caseNumber, second.caseNumber) ||
      first.id.localeCompare(second.id)
    );
  });
}

export interface PathologistReviewRowAppearance {
  key:
    | "rush"
    | "pending-stains"
    | "unsigned-addendum"
    | "sent-out"
    | "tc-review";
  label: string;
  background: string;
  marker: string;
  badgeColor: string;
}

const PATHOLOGIST_REVIEW_ROW_APPEARANCES = {
  rush: {
    key: "rush",
    label: "STAT or RUSH case",
    background: "var(--mantine-color-red-2)",
    marker: "STAT",
    badgeColor: "red",
  },
  pendingStains: {
    key: "pending-stains",
    label: "Pending stains",
    background: "var(--mantine-color-yellow-0)",
    marker: "STAINS",
    badgeColor: "yellow",
  },
  unsignedAddendum: {
    key: "unsigned-addendum",
    label: "Addendum (not signed out yet)",
    background: "var(--mantine-color-green-1)",
    marker: "ADDENDUM",
    badgeColor: "green",
  },
  sentOut: {
    key: "sent-out",
    label: "Case sent out",
    background: "var(--mantine-color-blue-1)",
    marker: "SENT OUT",
    badgeColor: "blue",
  },
  tcReview: {
    key: "tc-review",
    label: "TC technical review",
    background: "var(--mantine-color-yellow-0)",
    marker: "TC REVIEW",
    badgeColor: "yellow",
  },
} as const satisfies Record<string, PathologistReviewRowAppearance>;

export const PATHOLOGIST_REVIEW_COLOR_LEGEND = [
  PATHOLOGIST_REVIEW_ROW_APPEARANCES.rush,
  PATHOLOGIST_REVIEW_ROW_APPEARANCES.pendingStains,
  PATHOLOGIST_REVIEW_ROW_APPEARANCES.unsignedAddendum,
  PATHOLOGIST_REVIEW_ROW_APPEARANCES.sentOut,
] as const;

export function buildAllReviewColumns(
  visibleColumns: QueueColumnDef[]
): QueueColumnDef[] {
  const columns = visibleColumns.filter(
    (column) => column.id !== ASSIGNED_PATHOLOGIST_COLUMN.id
  );
  const physicianIndex = columns.findIndex(
    (column) => column.id === "physician"
  );
  const insertionIndex = physicianIndex >= 0 ? physicianIndex + 1 : 0;
  columns.splice(insertionIndex, 0, ASSIGNED_PATHOLOGIST_COLUMN);
  return columns;
}

export function buildAssignedReviewColumns(
  visibleColumns: QueueColumnDef[]
): QueueColumnDef[] {
  return [
    ...visibleColumns.filter(
      (column) => column.id !== TC_REVIEW_ACTION_COLUMN.id
    ),
    TC_REVIEW_ACTION_COLUMN,
  ];
}

function buildPathologistReviewQueueFilters(
  queueScope: PathologistReviewQueueScope,
  filters: CaseFilters
): PathologistReviewQueueFilters {
  return {
    queueScope,
    statusIds: filters.currentStatusIds,
    facilityIds: filters.facilityIds,
    workflowIds: filters.workflowIds,
    procedureIds: filters.procedureIds,
    patientIds: filters.patientIds,
    pathologistIds: filters.pathologistIds,
    physicianId: filters.physicianId,
    flagDefinitionIds: filters.flagDefinitionIds,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    dateCollected: filters.dateCollected,
    search: filters.search,
    isBlocked: filters.isBlocked,
    isRush: filters.isRush,
    isHighPriority: filters.isHighPriority,
  };
}

export function buildAssignedPathologistReviewQueueFilters(
  filters: CaseFilters
): PathologistReviewQueueFilters {
  return {
    ...buildPathologistReviewQueueFilters("assigned", filters),
    pathologistIds: undefined,
    prefixKeys: filters.prefixKeys,
  };
}

export function buildSentOutPathologistReviewQueueFilters(
  filters: CaseFilters
): PathologistReviewQueueFilters {
  return {
    ...buildAssignedPathologistReviewQueueFilters(filters),
    partition: "waiting",
  };
}

export function buildAllPathologistReviewQueueFilters(
  filters: CaseFilters
): PathologistReviewQueueFilters {
  return {
    ...buildPathologistReviewQueueFilters("all", filters),
    prefixKeys: filters.prefixKeys,
  };
}

export function applyQueueDisplayFilters(
  cases: PathologistReviewQueueCase[],
  filters: CaseFilters,
  overdueThresholdDays: number
): PathologistReviewQueueCase[] {
  return cases.filter(
    (caseItem) =>
      matchesCaseType(caseItem, filters) &&
      matchesTurnaroundTime(caseItem, filters, overdueThresholdDays) &&
      matchesPendingTests(caseItem, filters)
  );
}

export function hasActiveCaseFilters(filters: CaseFilters): boolean {
  return Object.values(filters).some((value) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.length > 0;
    return typeof value === "boolean" || typeof value === "number" || !!value;
  });
}

export function getPathologistReviewRowBackground(
  caseItem: PathologistReviewQueueCase
): string | undefined {
  return getPathologistReviewRowAppearance(caseItem)?.background;
}

export function getPathologistReviewRowAppearance(
  caseItem: PathologistReviewQueueCase
): PathologistReviewRowAppearance | undefined {
  if (caseItem.isRush) return PATHOLOGIST_REVIEW_ROW_APPEARANCES.rush;
  if ((caseItem.pendingAdditionalStainsCount ?? 0) > 0) {
    return PATHOLOGIST_REVIEW_ROW_APPEARANCES.pendingStains;
  }
  if (caseItem.hasUnsignedAddendum) {
    return PATHOLOGIST_REVIEW_ROW_APPEARANCES.unsignedAddendum;
  }
  if (
    caseItem.hasSentOut ||
    caseItem.sendOutReviewStatus === "pending_shipping"
  ) {
    return PATHOLOGIST_REVIEW_ROW_APPEARANCES.sentOut;
  }
  if (caseItem.reviewAction === "tc_completion") {
    return PATHOLOGIST_REVIEW_ROW_APPEARANCES.tcReview;
  }
  return undefined;
}

function matchesCaseType(
  caseItem: PathologistReviewQueueCase,
  filters: CaseFilters
): boolean {
  return !filters.caseType || caseItem.caseType === filters.caseType;
}

function matchesTurnaroundTime(
  caseItem: PathologistReviewQueueCase,
  filters: CaseFilters,
  overdueThresholdDays: number
): boolean {
  const turnaroundDays = caseItem.tatDays ?? 0;
  if (filters.tatOverdue) return turnaroundDays > overdueThresholdDays;
  if (filters.tatMinDays != null) return turnaroundDays >= filters.tatMinDays;
  return true;
}

function matchesPendingTests(
  caseItem: PathologistReviewQueueCase,
  filters: CaseFilters
): boolean {
  if (filters.hasPendingTests == null) return true;
  const hasPendingTests = (caseItem.pendingAdditionalStainsCount ?? 0) > 0;
  return hasPendingTests === filters.hasPendingTests;
}
